import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { IoClose, IoCheckmark } from "react-icons/io5";
import {
  materialUnitOptions,
  supplyApplyOptions,
  boqTypeOptions,
  normalizeSupplyApply,
  requiresLeadTime,
} from "../../../constants/boq";
import { updateBOQ } from "../../../services/budgetService";
import { isBoqUncovered } from "../../ProjectLevelBOQ/isBoqUncovered";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 44,
    height: 44,
    fontSize: 12,
    borderColor: "#d1d5db",
    borderRadius: 12,
    padding: 0,
  }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  indicatorsContainer: (base) => ({ ...base, height: 44 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, fontSize: 12 }),
};

const fieldWrap = { position: "relative", minWidth: 100, flex: 1 };
const inputStyle = {
  height: 44,
  width: "100%",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  padding: "8px 12px 0 12px",
  fontSize: 14,
  boxSizing: "border-box",
};
const labelStyle = {
  position: "absolute",
  left: 12,
  top: -8,
  fontSize: 11,
  color: "#6b7280",
  backgroundColor: "#fff",
  padding: "0 4px",
};

const ScopeInlineEditBoqRow = ({
  boq,
  inclusion,
  projectId,
  showUnitCostInput = false,
  isSiteEstimatesRoute = false,
  onBudgetUpdated,
  onCancel,
  isSaving: isSavingProp,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const existingUnitCostRaw =
    boq.type === "Activities" ? boq.manpower_rate : boq.unit_cost;
  const [unitCost, setUnitCost] = useState(
    existingUnitCostRaw != null ? String(existingUnitCostRaw) : ""
  );
  useEffect(() => {
    const next =
      boq.type === "Activities"
        ? boq.manpower_rate != null
          ? String(boq.manpower_rate)
          : ""
        : boq.unit_cost != null
        ? String(boq.unit_cost)
        : "";
    setUnitCost(next);
  }, [boq.type, boq.unit_cost, boq.manpower_rate]);

  const [item_name, setItemName] = useState(boq.item_name || "");
  const [boq_type, setBoqType] = useState(boq.type || "Materials");
  // Normalize legacy lowercase supply values so the dropdown selects the
  // right option and the lead-time logic fires.
  const [supply_apply, setSupplyApply] = useState(
    normalizeSupplyApply(boq.supply_type) || null
  );
  const [specification, setSpecification] = useState(boq.specification || "");
  const [material_unit, setMaterialUnit] = useState(boq.unit_of_measurement || null);
  const [quantity, setQuantity] = useState(boq.quantity != null ? String(boq.quantity) : "");
  const [days, setDays] = useState(boq.days != null ? String(boq.days) : "");
  const [leadTime, setLeadTime] = useState(
    boq.lead_time != null ? String(boq.lead_time) : ""
  );
  const showLeadTime = requiresLeadTime(supply_apply);

  // Image upload moved to read-only ScopeBOQItemRow/Card (mirrors portal).
  const isUncovered = isBoqUncovered(boq);

  const handleNumberChange = (value, isMaterials) => {
    const cleaned = value === "" ? "" : value.replace(/[^0-9]/g, "");
    if (cleaned === "" || (!isNaN(cleaned) && parseInt(cleaned) >= 0)) {
      if (isMaterials) {
        setQuantity(cleaned);
        setDays("");
      } else {
        setDays(cleaned);
        setQuantity("");
      }
    }
  };

  const handleSubmit = async () => {
    if (boq_type === "Materials" && (quantity == null || quantity === "")) {
      toast.error("Please enter quantity for materials");
      return;
    }
    if (boq_type === "Activities" && (days == null || days === "")) {
      toast.error("Please enter number of days for activity");
      return;
    }
    if (boq_type === "Materials" && !material_unit) {
      toast.error("Material Unit is required");
      return;
    }
    // Lead Time mandatory when Supply / Supply and Apply (reno-web rule)
    if (showLeadTime) {
      const trimmedLT = String(leadTime ?? "").trim();
      if (trimmedLT === "") {
        toast.error("Please enter Lead Time (days)");
        return;
      }
      if (!/^\d+$/.test(trimmedLT)) {
        toast.error("Lead Time must be a positive integer");
        return;
      }
    }
    if (showUnitCostInput) {
      const trimmed = String(unitCost ?? "").trim();
      if (isSiteEstimatesRoute) {
        if (trimmed === "") {
          toast.error("Please enter unit cost to proceed");
          return;
        }
        if (Number(trimmed) === 0) {
          toast.error("Enter a valid cost");
          return;
        }
      } else if (trimmed !== "" && Number(trimmed) === 0) {
        toast.error("Enter a valid cost");
        return;
      }
    }

    if (!projectId) {
      toast.error("Cannot save: project not found");
      return;
    }

    const unitCostFallback =
      boq_type === "Activities" ? boq.manpower_rate : boq.unit_cost;
    const unitCostValue = (() => {
      if (!showUnitCostInput) return unitCostFallback ?? null;
      const trimmed = String(unitCost ?? "").trim();
      if (trimmed !== "") return trimmed;
      return unitCostFallback ?? null;
    })();

    const payload = {
      name: item_name,
      boq_type,
      subcategory: boq.subcategory ?? "",
      supply_type: supply_apply,
      specification,
      lead_time:
        showLeadTime && leadTime != null && String(leadTime).trim() !== ""
          ? parseInt(String(leadTime).trim(), 10)
          : null,
      ...(boq_type === "Activities"
        ? {
            days,
            manpower_rate: unitCostValue,
            qty: null,
            material_unit_price: null,
          }
        : {
            material_unit,
            qty: quantity,
            material_unit_price: unitCostValue,
            days: null,
            manpower_rate: null,
          }),
    };

    try {
      setIsSaving(true);
      await updateBOQ(projectId, boq.id, payload);
      toast.success("BOQ updated");
      await onBudgetUpdated?.();
    } catch (e) {
      console.error(e);
      toast.error("Failed to update BOQ");
    } finally {
      setIsSaving(false);
    }
  };

  const saving = isSaving || isSavingProp;

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        gap: 16,
        backgroundColor: "#fff",
        border: `2px solid ${isUncovered ? "#f87171" : "#93c5fd"}`,
        borderRadius: 12,
        padding: 16,
        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
      }}
    >
      {/* Fields */}
      <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 12 }}>
          <div style={fieldWrap}>
            <input
              style={inputStyle}
              value={item_name}
              onChange={(e) => setItemName(e.target.value)}
            />
            <label style={labelStyle}>Item Name</label>
          </div>
          <div style={fieldWrap}>
            <Select
              value={supplyApplyOptions.find((o) => o.value === supply_apply) || null}
              onChange={(selected) => {
                const v = selected?.value || null;
                setSupplyApply(v);
                if (!requiresLeadTime(v)) setLeadTime("");
              }}
              options={supplyApplyOptions}
              styles={selectStyles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
            <label style={labelStyle}>Supply/Apply</label>
          </div>
          {showLeadTime && (
            <div style={fieldWrap}>
              <input
                type="text"
                inputMode="numeric"
                style={inputStyle}
                placeholder="Days"
                value={leadTime}
                onChange={(e) =>
                  setLeadTime(e.target.value.replace(/[^0-9]/g, ""))
                }
              />
              <label style={labelStyle}>Lead Time (days)</label>
            </div>
          )}
          <div style={fieldWrap}>
            <input
              style={inputStyle}
              placeholder={boq_type === "Activities" ? "Days" : "Qty"}
              value={boq_type === "Materials" ? quantity : days}
              onChange={(e) => handleNumberChange(e.target.value, boq_type === "Materials")}
              type="text"
              inputMode="numeric"
            />
            <label style={labelStyle}>{boq_type === "Activities" ? "Days" : "Quantity"}</label>
          </div>
          {boq_type === "Materials" && (
            <div style={fieldWrap}>
              <Select
                value={materialUnitOptions.find((opt) => opt.value === material_unit) || null}
                onChange={(selected) => setMaterialUnit(selected?.value || null)}
                options={materialUnitOptions}
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              />
              <label style={labelStyle}>Material Unit</label>
            </div>
          )}
          {showUnitCostInput && (
            <div style={fieldWrap}>
              <input
                type="text"
                inputMode="numeric"
                value={unitCost}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === "" || /^\d+$/.test(v)) setUnitCost(v);
                }}
                placeholder="Enter Unit Cost"
                style={inputStyle}
              />
              <label style={labelStyle}>Unit Cost</label>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
            <Select
              value={{ label: boq_type, value: boq_type }}
              onChange={(selected) => {
                const newType = selected?.value || "Materials";
                setBoqType(newType);
                if (newType === "Materials") {
                  setDays("");
                } else {
                  setQuantity("");
                  setMaterialUnit(null);
                }
              }}
              options={boqTypeOptions}
              styles={selectStyles}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
            <label style={labelStyle}>Material Type</label>
          </div>
          <div style={{ flex: 2, position: "relative", minWidth: 0 }}>
            <input
              style={inputStyle}
              placeholder="Enter specification"
              value={specification}
              onChange={(e) => setSpecification(e.target.value)}
            />
            <label style={labelStyle}>Specifications</label>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: "flex", flexDirection: "row", gap: 12, alignItems: "center", flexShrink: 0 }}>
        <button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            padding: 8,
            color: "#16a34a",
            background: "transparent",
            border: "none",
            borderRadius: 8,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.5 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Save"
        >
          <IoCheckmark style={{ width: 20, height: 20 }} />
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          style={{
            padding: 8,
            color: "#ef4444",
            background: "transparent",
            border: "none",
            borderRadius: 8,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.5 : 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Cancel"
        >
          <IoClose style={{ width: 20, height: 20 }} />
        </button>
      </div>
    </div>
  );
};

export default ScopeInlineEditBoqRow;
